import {
  ActionEvent,
  FeedbackEvent,
  TrackingEvent,
  EventPayload,
  EventContext,
  ChatFinishEvent,
  LinkClickEvent,
} from "@zapier/chatbots-tailwind";
import { CreateChatbotEventInput, CreateChatbotEventOutput } from "utils/trpc";
import { toast } from "@/components/Toaster/Toaster";
import { TrackingContextProps } from "observability/tracking/TrackingProvider";
import { encodingForModel, TiktokenModel } from "./utils/tikToken";
import { handleChatbotError } from "./handleChatbotError";
import { ConversationEndEvent } from "@zapier/chatbots-tailwind/dist/types";
import { ConsumerProject } from "@/server/schemas/projects";

interface HandleChatbotEventOptions<TEvent extends EventPayload>
  extends Pick<
    TrackingContextProps,
    | "emitChatbotAITokenUsageReceivedEvent"
    | "emitAiTrackingPredictionUserFeedbackEvent"
    | "emitConsumerInteractionEvent"
    | "emitChatbotsConsumerInteractionEvent"
  > {
  context: EventContext;
  payload: TEvent;
  project: ConsumerProject;
  projectId?: string;
  pageId?: string;
  blockId?: string;
  isEmbedded: boolean;
  isFree: boolean;
  createChatbotEvent: (
    input: CreateChatbotEventInput
  ) => Promise<CreateChatbotEventOutput>;
}

/**
 * This function is an attempt to isolate the logic for handling chatbot events.
 * Way too many things are happening on the frontend here. Ideally this logic is
 * moved to the backend.
 */
export const handleChatbotEvent = async (
  options: HandleChatbotEventOptions<EventPayload>
) => {
  switch (options.payload.type) {
    case "action": {
      await handleChatbotAction({
        ...options,
        payload: options.payload,
      });
      break;
    }
    case "link-click": {
      await handleChatbotLinkClick({
        ...options,
        payload: options.payload,
      });
      break;
    }
    case "conversation-end": {
      await handleChatbotConversationEnd({
        ...options,
        payload: options.payload,
      });
      break;
    }
    case "feedback": {
      await handleChatbotFeedback({
        ...options,
        payload: options.payload,
      });
      break;
    }
    case "tracking": {
      handleChatbotTracking({
        ...options,
        payload: options.payload,
      });
      break;
    }
    case "chat-finish": {
      await handleChatbotChatFinish({
        ...options,
        payload: options.payload,
      });
      break;
    }
    case "error":
    case "chat-error": {
      handleChatbotError(options.payload.error, options.context.chatbot);
      break;
    }
  }
};

function findOrThrow<T>(searchIn: T[], predicate: (item: T) => boolean): T {
  const item = searchIn.find(predicate);
  if (!item) {
    throw new Error("Item not found");
  }
  return item;
}

async function handleChatbotAction(
  options: HandleChatbotEventOptions<ActionEvent>
) {
  const {
    payload: { chatbotId, messageId, actionId, sessionId },
    context: {
      chatbot: { name, buttons },
      messages,
    },
    createChatbotEvent,
  } = options;
  const message = findOrThrow(messages, (m) => m.id === messageId);
  const index = messages.indexOf(message);
  const prevMessage = messages[index - 1];
  const action = findOrThrow(buttons, (b) => b.id === actionId);

  await createChatbotEvent({
    chatbotId,
    event: {
      type: "ChatbotButtonClick",
      actionId,
      chatbotSessionId: sessionId,
      values: {
        response: message.content,
        chatbotName: name,
        prompt: prevMessage?.content,
      },
    },
  });

  toast.success({ message: action.notificationText });
}

async function handleChatbotLinkClick(
  options: HandleChatbotEventOptions<LinkClickEvent>
) {
  const {
    payload: { chatbotId, sessionId, linkUrl, isButtonClick },
    createChatbotEvent,
  } = options;

  await createChatbotEvent({
    chatbotId,
    event: {
      type: "ChatbotLinkClick",
      chatbotId,
      chatbotSessionId: sessionId,
      values: {
        linkUrl,
        isButtonClick,
      },
    },
  });
}

async function handleChatbotConversationEnd(
  options: HandleChatbotEventOptions<ConversationEndEvent>
) {
  const {
    payload: { chatbotId, sessionId },
    createChatbotEvent,
  } = options;

  await createChatbotEvent({
    chatbotId,
    event: {
      type: "ChatbotConversationEnd",
      chatbotId,
      chatbotSessionId: sessionId,
    },
  });
}

async function handleChatbotFeedback(
  options: HandleChatbotEventOptions<FeedbackEvent>
) {
  const {
    payload: { chatbotId, sessionId, messageId, rating, comment },
    context: { messages },
    createChatbotEvent,
  } = options;

  const { content } = findOrThrow(messages, (m) => m.id === messageId);

  const conversation = messages.reduce(
    (acc, m) =>
      m.role === "user" || m.role === "assistant"
        ? acc.concat({
            role: m.role,
            content: m.content,
          })
        : acc,
    [] as { role: "user" | "assistant"; content: string }[]
  );

  await createChatbotEvent({
    chatbotId,
    event: {
      type: "ChatbotFeedback",
      chatbotId,
      chatbotSessionId: sessionId,
      values: {
        message: content,
        conversation,
        rating,
        comment,
      },
    },
  });
}

function handleChatbotTracking(
  options: HandleChatbotEventOptions<TrackingEvent>
) {
  const {
    payload: { chatbotId, fields },
    project: { isHidden },
    projectId,
    pageId,
    emitAiTrackingPredictionUserFeedbackEvent,
    emitChatbotsConsumerInteractionEvent,
    emitConsumerInteractionEvent,
  } = options;

  if ("prediction_id" in fields) {
    return emitAiTrackingPredictionUserFeedbackEvent(
      fields as Parameters<typeof emitAiTrackingPredictionUserFeedbackEvent>[0]
    );
  }

  if (chatbotId) {
    emitChatbotsConsumerInteractionEvent({
      ...fields,
      chatbot_id: chatbotId,
    } as Parameters<typeof emitChatbotsConsumerInteractionEvent>[0]);
  }

  if (!isHidden) {
    emitConsumerInteractionEvent({
      ...fields,
      interfaces_project_id: projectId,
      interfaces_page_id: pageId,
    } as Parameters<typeof emitConsumerInteractionEvent>[0]);
  }
}

async function handleChatbotChatFinish(
  options: HandleChatbotEventOptions<ChatFinishEvent>
) {
  const {
    context: {
      chatbot: { authenticationId, model },
      messages,
    },
    payload: {
      message: { id: messageId, content },
    },
    project: {
      creator: { id, zapierId },
    },
    projectId,
    pageId,
    blockId,
    isEmbedded,
    isFree,
    emitChatbotAITokenUsageReceivedEvent,
  } = options;

  if (blockId && projectId && pageId) {
    const enc = await encodingForModel(model as TiktokenModel);

    // find the current message so we can get the previous message
    const message = findOrThrow(messages, (m) => m.id === messageId);
    const index = messages.indexOf(message);
    const prevMessage = messages[index - 1];

    // how many tokens did we use on the prompt?
    const prompt_token_quantity = enc.encode(prevMessage.content).length;

    // how many tokens did we use on the completion?
    const completion_token_quantity = enc.encode(content).length;

    if (prompt_token_quantity) {
      emitChatbotAITokenUsageReceivedEvent({
        completion_token_quantity,
        current_project_owner_creator_id: id,
        current_project_owner_customuser_id: zapierId,
        current_project_owner_plan_type: isFree ? "free" : "paid",
        consumer_customuser_id: null,
        embedded_flag: isEmbedded,
        interfaces_component_id: blockId,
        interfaces_page_id: pageId,
        interfaces_project_id: projectId,
        model_name: model,
        authentication_id: authenticationId,
        prompt_token_quantity,
        total_token_quantity: prompt_token_quantity + completion_token_quantity,
      });
    }
  }
}
